// Widget central de edição. Dois modos (decisão do piloto — "ver marcado / clicar
// para editar"):
//   - REPOUSO: mostra o texto clínico com o token [?] realçado em âmbar; clicar
//     entra em edição.
//   - EDIÇÃO: textarea de texto puro (o médico vê e edita o [?] como texto). Ao
//     sair (blur), volta ao repouso e o âmbar é re-renderizado.
// O cursor fica previsível (textarea nativo) e o âmbar nunca é editável por acidente.

import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { segmentar } from '../../lib/marcadores';
import { cor, fonte } from '../../styles/tokens';

// Corpo do campo: mesma métrica do mockup (serif 14.5 / 1.62, padding 11×14).
export const corpoTexto: CSSProperties = {
  padding: '11px 14px',
  fontFamily: fonte.serif,
  fontSize: 14.5,
  lineHeight: 1.62,
  color: cor.texto,
};

// Token [?] realçado — só o marcador, não a frase (a fronteira não é conhecida).
const marcadorStyle: CSSProperties = {
  background: cor.ambarBg,
  color: cor.ambarTexto,
  borderBottom: `2px solid ${cor.ambarBorda}`,
  padding: '0 3px',
  fontFamily: fonte.mono,
  fontSize: 11,
  fontWeight: 700,
};

interface Props {
  valor: string;
  onChange: (valor: string) => void;
  editavel?: boolean;
  placeholder?: string;
}

export function TextoComMarcadores({
  valor,
  onChange,
  editavel = true,
  placeholder,
}: Props): JSX.Element {
  const [editando, setEditando] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editando) {
      const ta = taRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
      }
    }
  }, [editando]);

  if (editando) {
    return (
      <textarea
        ref={taRef}
        value={valor}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditando(false)}
        style={{
          ...corpoTexto,
          width: '100%',
          border: 'none',
          outline: `2px solid ${cor.azul}`,
          outlineOffset: -2,
          resize: 'vertical',
          minHeight: 88,
          background: cor.branco,
        }}
      />
    );
  }

  const vazio = valor === '';
  return (
    <div
      onClick={editavel ? () => setEditando(true) : undefined}
      title={editavel ? 'Clique para editar' : undefined}
      style={{ ...corpoTexto, cursor: editavel ? 'text' : 'default', whiteSpace: 'pre-wrap' }}
    >
      {vazio ? (
        <span style={{ color: cor.muted }}>{placeholder ?? '—'}</span>
      ) : (
        segmentar(valor).map((s, i) =>
          s.tipo === 'texto' ? (
            <span key={i}>{s.valor}</span>
          ) : (
            <mark key={i} style={marcadorStyle}>
              [?]
            </mark>
          ),
        )
      )}
    </div>
  );
}
